import {
  Component,
  Host,
  h,
  Prop,
  State,
  Element,
  Event,
  EventEmitter
} from "@stencil/core";
import { getIconPath, MercuryBundles } from "@genexus/mercury";
import { ComboBoxModel } from "@genexus/chameleon-controls-library";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/button",
  "components/icon",
  "components/checkbox",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar",
  "components/combo-box"
];

const CHEVRON_DOWN_ICON = getIconPath({
  category: "navigation",
  name: "chevron-down",
  colorType: "on-interactive"
});

const CLOSE_ICON = getIconPath({
  category: "system",
  name: "close",
  colorType: "on-interactive"
});

@Component({
  tag: "gx-ide-multi-select",
  styleUrl: "gx-ide-multi-select.scss",
  shadow: true
})
export class GxIdeMultiSelect {
  #multiButtonEl: HTMLButtonElement;
  #popoverEl: HTMLChPopoverElement;

  @Element() el: HTMLGxIdeMultiSelectElement;

  @State() showPopover = false;

  /**
   * Model with available options to select from
   */
  @Prop() readonly model: ComboBoxModel = [];

  /**
   * Currently selected item values
   */
  @Prop() readonly value: string[] = [];

  /**
   * Placeholder text when no items are selected
   */
  @Prop() readonly placeholder: string = "Select options";

  /**
   * Disabled state
   */
  @Prop() readonly disabled: boolean = false;

  /**
   * Emitted when the selection changes
   */
  @Event() gxSelectionChange: EventEmitter<string[]>;

  componentDidLoad() {
    if (this.#popoverEl && this.#multiButtonEl) {
      this.#popoverEl.actionElement = this.#multiButtonEl;
    }
  }

  #togglePopover = () => {
    if (!this.disabled) {
      this.showPopover = !this.showPopover;
    }
  };

  #checkboxChangeHandler = (
    optionValue: string,
    event: CustomEvent | InputEvent
  ) => {
    const target = event.target as HTMLChCheckboxElement;
    const isChecked = target.value === target.checkedValue;

    let newValue: string[];
    if (isChecked) {
      newValue = [...this.value, optionValue];
    } else {
      newValue = this.value.filter(val => val !== optionValue);
    }

    this.gxSelectionChange.emit(newValue);
  };

  #removeSelectedItemHandler = (itemValue: string) => {
    const newValue = this.value.filter(val => val !== itemValue);
    this.gxSelectionChange.emit(newValue);
  };

  #isItemSelected = (itemValue: string): boolean => {
    return this.value.includes(itemValue);
  };

  #renderSelectedItems = () => {
    if (this.value.length === 0) {
      return (
        <span class="multi-select__placeholder body-italic-m">
          {this.placeholder}
        </span>
      );
    }

    const selectedOptions = this.model.filter(opt =>
      this.value.includes(opt.value)
    );

    return (
      <div class="multi-select__items">
        {selectedOptions.map(item => (
          <div class="multi-select__item" key={item.value}>
            <span class="multi-select__caption body-regular-s">
              {item.caption}
            </span>
            <button
              type="button"
              class="button-tertiary button-icon-only multi-select__close"
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                this.#removeSelectedItemHandler(item.value);
              }}
              aria-label={`Remove ${item.caption}`}
            >
              <ch-image class="icon-xs" src={CLOSE_ICON} type="mask"></ch-image>
            </button>
          </div>
        ))}
      </div>
    );
  };

  render() {
    return (
      <Host>
        <ch-theme model={CSS_BUNDLES}></ch-theme>

        <button
          class="combo-box multi-select__button"
          type="button"
          ref={(el: HTMLButtonElement) => (this.#multiButtonEl = el)}
          onClick={this.#togglePopover}
          disabled={this.disabled}
        >
          {this.#renderSelectedItems()}
          <ch-image
            class="icon-m multi-select__chevron"
            src={CHEVRON_DOWN_ICON}
            type="mask"
          />
        </button>

        <ch-popover
          ref={(el: HTMLChPopoverElement) => (this.#popoverEl = el)}
          closeOnClickOutside
          show={this.showPopover}
          blockAlign="outside-end"
          inlineAlign="inside-end"
          inlineSizeMatch="action-element"
          class={"scrollable"}
        >
          <div>
            {this.model.map(option => (
              <ch-checkbox
                class="checkbox checkbox-item"
                caption={option.caption}
                checkedValue="true"
                value={this.#isItemSelected(option.value) ? "true" : "false"}
                startImgSrc={option.startImgSrc}
                onInput={e => this.#checkboxChangeHandler(option.value, e)}
              />
            ))}
          </div>
        </ch-popover>
      </Host>
    );
  }
}
